<template>
  <Modal ref="modalRef" position="end" @after-close="onAfterClose">
    <template #header>
      <Text variant="h3">Conversation</Text>
    </template>

    <template #body>
      <View v-if="item" direction="column" gap="lg" class="sidebar-body">
        <!-- Renter -->
        <View justify="space-between" align="center" gap="md">
          <View gap="sm" align="center" class="renter">
            <Avatar :user-name="renterName" size="medium" />
            <View direction="column">
              <Text variant="label-large" as="span">{{ renterName }}</Text>
              <Text v-if="item.leadSource" variant="body-small" color="secondary" as="span">
                from {{ item.leadSource }}
              </Text>
            </View>
          </View>

          <Badge v-if="outcomeLabel" :label="outcomeLabel" :variant="outcomeVariant ?? 'grey'" />
        </View>

        <Divider />

        <!-- Summary -->
        <Card variant="tertiary" size="medium">
          <View direction="column" gap="sm">
            <View justify="space-between" align="center" gap="sm">
              <Text variant="h4">Summary</Text>
              <Text v-if="outcomeLabel" variant="label-medium" as="span" :class="outcomeTextClass">
                {{ outcomeLabel }}
              </Text>
            </View>

            <Text variant="body-medium" color="secondary">
              {{ item.summary || 'No summary available yet.' }}
            </Text>

            <ConversationSummaryHighlights :items="item.feedbackItems" :is-actionable="item.isActionable" />
          </View>
        </Card>

        <!-- Chat -->
        <View direction="column" gap="md" class="chat">
          <Text variant="h4">Chat</Text>

          <View v-if="isChatPending" justify="center" padding-block="lg">
            <LoadingIndicator />
          </View>

          <Text v-else-if="chatError" variant="body-medium" color="tertiary">
            Couldn’t load this conversation. Please try again.
          </Text>

          <Text v-else-if="!messages.length" variant="body-medium" color="tertiary">No messages yet.</Text>

          <View v-else direction="column" gap="lg">
            <ConversationChatMessage v-for="message in messages" :key="message.id" :message="message" />
          </View>
        </View>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { Avatar, Badge, BadgeVariant, Card, Divider, LoadingIndicator, Modal, Text, View } from '@tallo/design-system';

import { MessageDto } from '../models/message.interface';
import { useRenterChatTranscriptQuery } from '../queries/renter-chat-transcript.query';
import ConversationChatMessage from './conversation-chat-message.vue';
import ConversationSummaryHighlights from './conversation-summary-highlights.vue';

interface ConversationSummaryFeedbackItem {
  category: string;
  details: string | null;
  type: string;
  isActionable: boolean;
  priority: string;
}

/**
 * Structural shape of a convo summary (a subset of `PropertyConvoSummaryDto`). Declared locally so the
 * conversation lib does not import `@tallo/investor/property` — that would create a circular dependency
 * (property already imports this lib for `ConversationDto`).
 */
interface ConversationSummaryItem {
  feedbackId: string;
  renterId: string;
  renterUserId?: string | null;
  renterName: string | null;
  leadSource?: string | null;
  summary: string | null;
  currentOutcome: string | null;
  isActionable: boolean;
  feedbackItems: ConversationSummaryFeedbackItem[];
}

const props = defineProps<{
  propertyId: string;
  item: ConversationSummaryItem | null;
  // Outcome label/variant are derived from `currentOutcome` by the caller (which owns the property-side
  // heuristic), keeping a single source of truth without a property → conversation dependency.
  outcomeLabel?: string | null;
  outcomeVariant?: BadgeVariant;
}>();

const modalRef = ref<InstanceType<typeof Modal>>();
const isOpen = ref(false);

const renterName = computed(() => props.item?.renterName || 'Renter');
const outcomeTextClass = computed(() => (props.outcomeVariant === 'green' ? 'outcome-positive' : 'outcome-neutral'));

const {
  data: chatData,
  isPending: isChatPending,
  error: chatError,
} = useRenterChatTranscriptQuery(
  () => props.propertyId,
  () => props.item?.renterUserId,
  () => isOpen.value,
);

const messages = computed<MessageDto[]>(() =>
  (chatData.value ?? [])
    .flatMap((conversation) => conversation.messages ?? [])
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
);

function open(): void {
  isOpen.value = true;
  modalRef.value?.show();
}

function close(): void {
  modalRef.value?.close();
}

function onAfterClose(): void {
  isOpen.value = false;
}

defineExpose({ open, close });
</script>

<style scoped lang="scss">
.sidebar-body {
  width: 100%;
}

.renter {
  min-width: 0;
}

.outcome-positive {
  color: var(--t-color-green-600);
}

.outcome-neutral {
  color: var(--t-text-color-secondary);
}
</style>
